import { IBaseEntity, INode } from './base';
import { IText, ITextDAO } from './text';
import { IImage, IImageDAO } from './image';
import { IModule, IModuleBasicDAO, IModuleDAO } from './module';
import { IVerificationDAO } from './verification';
import { ICertificate } from './certificate';
import { IGroup } from './group';
import { IService } from './service';
import { IOrganisationSystemDAO, ISystem } from './system';
import { IVariableDAO } from './variable';
import { CountByDate } from './organisation';
import { ICoursePermission, ICourseProgress, ICourseProgressDAO } from './permissions';
import { EntityTypes } from 'constants/entities';
import { IOrgUserVariable, IOrgUserVariableDAO } from './user';
import { ISessionAttempt } from './session';

export interface ICourse extends IBaseEntity {
  /**
   * The store holds the whole course, not just the index row.
   *
   * `list COU` returns the course's own columns and its stats; only `get COU`
   * brings the nodes, outs and variables the section pages render. The flag
   * says which of the two put this record here, so a page can tell "we have
   * this course" from "we have enough of this course" — set by merge(), never
   * by the API. See CourseShellLayout, which skips the fetch on a deep one.
   */
  __deep?: boolean;
  copyId: number;
  name: string;
  textId: number;
  imageId: number;
  folderId: number;
  variableId: number;
  type: CourseType;
  status: CourseStatus;
  /**
   * Editing freeze. The API sets it on the course when a draft goes live, and
   * while it is true every content save under the course (nodes, modules,
   * actions) is refused — for everybody, the holder included. Only the holder
   * (or a super admin) may clear it. See CourseLock/useCourseLock.
   */
  locked?: boolean;
  /**
   * Organisation user who holds the lock. Not guaranteed to be present on a
   * locked course — every course update writes the column back from the
   * client's payload — so consumers fall back to `modified.userId`
   * (useCourseLock does).
   */
  lockedBy?: number;
  launchCampaign: boolean;
  launchDate: string;
  progress: number;
  courseNodeId: number;
  courseProgressCount?: number;
  courseProgressActiveCount: number;
  courseProgressCompleteCount: number;
  courseProgressCountChange?: number;
  courseProgressMobileCount?: number;
  courseProgressActiveCountChange?: number;
  courseProgressCompleteCountChange?: number;
  organisationUserVariableCount: number;
  statsTotalTime: number;
  /**
   * Sum of `statsTime` over completed progresses only, so
   * `statsTotalCompletedTime / courseProgressCompleteCount` is the average time
   * a finisher took. `statsTotalTime` spans everyone, finished or not.
   */
  statsTotalCompletedTime?: number;
  forumTopicCount?: number;
  moduleCount?: number;
}

export interface ICourseDAO extends ICourse {
  /**
   * Marks the payload as the whole course rather than an index row. Set only by
   * `get COU`; merge() turns it into the sticky `__deep` on the stored entity.
   */
  deep?: boolean;
  text: IText;
  image: IImageDAO;
  outs: ICourseOut[];
  nodes: ICourseNode[];
  groups: IGroup[];
  courseVariables: ICourseVariableDAO[];
  coursePermission?: ICoursePermission;
  latestCourseProgress?: ICourseProgress;
}

export enum CourseType {
  STANDARD = 0,
}
export enum CourseStatus {
  /** Soft-deleted. The API keeps the row and drops it from every list. */
  DELETED = -1,
  DRAFT = 0,
  LIVE = 1,
  PAUSED = 2,
}

export interface ICourseNode extends IBaseEntity {
  type: CourseNodeType;
  status: CourseNodeStatus;
  entityType: CourseNodeRole;
  courseId: number;
  /** On a module's review link: the course node it opens at. */
  parentId?: number | null;
  childId: number;
  nodeId: number;
  moduleId: number;
  moduleVersionId: number;
  verificationId: number;
  systemId: number;
  organisationSystemId: number;
  variableId: number;
  serviceId: number;
  groupId: number;
  certificateId: number;
  textId: number;
  imageId: number;
  uploadId?: string;
  token?: string;
  prettyToken?: string;
  iconId: number;
  learnerCount?: number;
  certificateCount?: number;
  certificateCompleteCount?: number;
  variableCount?: number;
  responseNewCount?: number;
  joinCourseNode: ICourseNode;
  delay: number;
  organisationUserVariablesCount: number;
  sessionAttemptCount: number;
  sessionAttemptProgressCount: number;
  sessionAttemptCompleteCount: number;
  sessionAttemptTotalTime: number;
  sessionAttemptTotalProgress: number;
  courseProgressCount: number;
  variableTotalScore: number;
  home?: number;
  themeId?: number;
  courseProgressCurrent?: ICourseProgressDAO[];
  courseProgressEntry?: ICourseProgressDAO[];
  limit?: number;
  value?: number;
}

export enum CourseNodeType {
  START = 0,
  ASSIGN = 1,
  LOGIC = 2,
  FILTER = 3,
  REQUEST = 4,
  RESPONSE = 5,
  RANDOM = 6,
  END = 7,
  NOTIFICATION = 8,
  INTEGRATION = 9,
  MENU = 10,
}

export enum CourseNodeRole {
  ACCESS = -1,
  MODULE = 0,
  GROUP = 1,
  VARIABLE = 2,
  VERIFICATION = 3,
  SYSTEM = 4,
  SERVICE = 5,
  TEXT = 6,
  SCENE = 7,
  DIALOGUE = 8,
  SHARE = 9,
  SCORM = 10,
  API = 11,
  EMAIL = 12,
  PHOTO = 13,
  VIDEO = 14,
  NAME = 15,
  SYSTEM_ID = 16,
  DOCUMENT = 17,
  CERTIFICATE = 18,
  ACCOUNT = 19,
  MENU = 20,
  VARIABLE_SINGLE = 21,
  ID_VERIFICATION = 22,
  STANDDOWN = 23,
  /** A course's review link: a private START node outside the flow. */
  REVIEW = 24,
}

export enum CourseNodeStatus {
  DELETED = -1,
  DRAFT = 0,
  PRIVATE = 1,
  PUBLIC = 2,
  IDENTITY = 3,
  SELF_SIGNUP = 4,
  ARCHIVED = 5,
  INHERIT = 6,
}

/**
 * The audiences a step's own link can be set to from the admin.
 *
 * `status` is one field doing two jobs — live/draft, and who the direct link
 * works for — and it holds two more audiences than any control here offers.
 * A step already set to one of those has no option to show as chosen, so the
 * list has to be able to say it is looking at a value it did not set rather
 * than render nothing and quietly replace it on the next click.
 */
export const LINK_AUDIENCES = [CourseNodeStatus.INHERIT, CourseNodeStatus.PUBLIC, CourseNodeStatus.PRIVATE];

export interface ICourseNodeDAO extends ICourseNode {
  courseId: number;
  node: INode;
  module: IModuleBasicDAO;
  verification: IVerificationDAO;
  system: ISystem;
  organisationSystem: IOrganisationSystemDAO;
  variable: IVariableDAO;
  service: IService;
  group: IGroup;
  certificate: ICertificate;
  text: ITextDAO;
  image: IImageDAO;
  entities: ICourseNodeEntityDAO[];
  courseOuts: ICourseOutDAO[];
  isDefault?: boolean;
  courseVariable?: ICourseVariable;
  organisationUserVariableId?: number;
  /** A document or image request in the player: `UploadRequestState` — new, waiting for review, or sent back. */
  requestState?: number;
  /** Why a reviewer sent the upload back. */
  reviewNote?: string | null;
  sessionAttempt?: ISessionAttempt;
  organisationUserVariable?: IOrgUserVariable;
  organisationUserVariables: IOrgUserVariable[];
  courseVariables: ICourseVariable[];
  courseUserVariables: ICourseUserVariable[];
  courseNodeTargets: ICourseOut[];
  courseNodeGroups: ICourseNodeGroupDAO[];
  avgSessionAttemptTime: number | null;
  isVerificationRecheck?: boolean;
  /**
   * Player only: a course reviewer reached a step that holds a learner (payment,
   * ID check, stand-down). They see it with a note and Continue past it.
   */
  reviewSkip?: boolean;
}

export interface ICourseFlowNode {
  id: string;
  depth: number;
  data: any;
  children: ICourseFlowNode[];
  index: number;
  source: string;
  sourceHandle: string;
  type: EntityTypes;
  height: number;
  position: { x: number; y: number };
}

export interface IModuleCourseNode extends ICourseNode {
  node: INode;
  text: ITextDAO;
  image: IImageDAO;
  module: IModuleDAO;
}

export interface ICourseOut extends IBaseEntity {
  sourceId: number;
  sourceHandle: number;
  targetId: number;
  targetHandle: number;
  delay: number;
  status: CourseOutStatus;
  start?: number;
  end?: number;
  courseId: number;
}

export interface ICourseNodeGroup extends IBaseEntity {
  courseNodeId: number;
  groupId: number;
}
export interface ICourseNodeGroupDAO extends ICourseNodeGroup {
  group: IGroup;
}

export interface ICourseOutDAO extends ICourseOut {
  source?: ICourseNodeDAO;
  target?: ICourseNodeDAO;
}

export enum CourseOutType {
  STANDARD = 0,
  DELAY = 1,
  REMINDER = 2,
}
export enum CourseOutStatus {
  NEW = 0,
  COMPLETE = 1,
  PROCESSED = 2,
}

export interface ICourseNodeEntity extends IBaseEntity {
  type: CourseNodeEntityType;
  courseNodeId: number;
  textId: number;
  order: number;
  verificationId: number;
  variableId: number;
  variableOptionId: number;
  groupId: number;
  certificateId: number;
  name: string;
  operator: number;
  value: number;
  delay: number;
  minValue: number;
  maxValue: number;
  outs: ICourseOut[];
}

export interface ICourseNodeEntityDAO extends ICourseNodeEntity {
  group: IGroup;
  variable: IVariableDAO;
  text: IText;
}

export enum CourseNodeEntityType {
  BRANCH = 0,
  REMIND = 1,
  /** A document or image request's settings — see `types/domain/uploadRequest`. */
  UPLOAD = 2,
}

export interface ICourseReport extends ICourse {
  range: string;
  invitesByDate: CountByDate[];
  startedByDate: CountByDate[];
  completedByDate: CountByDate[];
  totalPeople: number;
  totalNotStarted: number;
  totalStarted: number;
  totalCompleted: number;
}

export interface ICourseVariable extends IBaseEntity {
  certificateId: number;
  courseId: number;
  variableId: number;
  variableOptionId: number;
  value: number;
  order: number;
}

export interface ICourseVariableDAO extends ICourseVariable {
  variable: IVariableDAO;
  modules: IModule[];
  variableCount?: number;
  variableCompleteCount?: number;
  variableTotalScore: number;
}

export interface ICourseVariableReport extends ICourseVariableDAO {}

export interface ICourseUserVariable extends IBaseEntity {
  courseNodeId: number;
  organisationUserVariableId: number;
  courseVariableId: number;
  organisationUserVariable: IOrgUserVariable;
  courseVariable: ICourseVariable;
}

export interface ICourseUserVariableDAO extends ICourseUserVariable {
  organisationUserVariable: IOrgUserVariableDAO;
  courseVariable: ICourseVariableDAO;
}
